import React, { useState, useEffect } from 'react'
import { lumi } from '../lib/lumi'
import {Upload, Save, Trash2, Eye, EyeOff, Plus} from 'lucide-react'

interface ProblemKeyword {
  _id: string
  keyword: string
  description: string
  imageUrl: string
  order: number
  active: boolean
  createdAt: string
  updatedAt: string
}

interface ProblemsKeywordsManagerProps {
  onClose?: () => void
  embedded?: boolean
}

export function ProblemsKeywordsManager({ onClose, embedded }: ProblemsKeywordsManagerProps) {
  const [keywords, setKeywords] = useState<ProblemKeyword[]>([])
  const [loading, setLoading] = useState(true)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [formData, setFormData] = useState({
    keyword: '',
    description: '',
    imageUrl: '',
    active: true
  })
  const [uploading, setUploading] = useState(false)
  const [showAddForm, setShowAddForm] = useState(false)

  useEffect(() => {
    loadKeywords()
  }, [])

  const loadKeywords = async () => {
    try {
      setLoading(true)
      const result = await lumi.entities.problem_keywords.list({
        sort: { order: 1 }
      })
      setKeywords(result.list || [])
    } catch (error) {
      console.error('Erreur chargement mots-clés:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleFileUpload = async (file: File, keywordId?: string) => {
    try {
      setUploading(true)
      const uploadResults = await lumi.tools.file.upload([file])
      
      if (!uploadResults || uploadResults.length === 0) {
        throw new Error('Aucun résultat d\'upload reçu')
      }
      
      const uploadResult = uploadResults[0]
      
      if (uploadResult.uploadError) {
        throw new Error(uploadResult.uploadError)
      }
      
      if (!uploadResult.fileUrl) {
        throw new Error('URL du fichier manquante')
      }
      
      if (keywordId) {
        await lumi.entities.problem_keywords.update(keywordId, {
          imageUrl: uploadResult.fileUrl,
          updatedAt: new Date().toISOString()
        })
      } else {
        setFormData({ ...formData, imageUrl: uploadResult.fileUrl })
      }
      
      await loadKeywords()
      alert('✅ Image uploadée avec succès !')
    } catch (error: any) {
      console.error('Erreur upload:', error)
      alert(`❌ ${error?.message || 'Erreur lors de l\'upload'}`)
    } finally {
      setUploading(false)
    }
  }

  const handleCreate = async () => {
    if (!formData.keyword || !formData.description || !formData.imageUrl) {
      alert('⚠️ Veuillez remplir tous les champs et uploader une image')
      return
    }

    try {
      await lumi.entities.problem_keywords.create({
        keyword: formData.keyword,
        description: formData.description,
        imageUrl: formData.imageUrl,
        order: keywords.length + 1,
        active: formData.active,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })
      
      setFormData({ keyword: '', description: '', imageUrl: '', active: true })
      setShowAddForm(false)
      await loadKeywords()
      alert('✅ Mot-clé créé avec succès !')
    } catch (error) {
      console.error('Erreur création:', error)
      alert('❌ Erreur lors de la création')
    }
  }

  const handleUpdate = async (keywordId: string) => {
    try {
      await lumi.entities.problem_keywords.update(keywordId, {
        keyword: formData.keyword,
        description: formData.description,
        imageUrl: formData.imageUrl,
        active: formData.active,
        updatedAt: new Date().toISOString()
      })
      
      setEditingId(null)
      setFormData({ keyword: '', description: '', imageUrl: '', active: true })
      await loadKeywords()
      alert('✅ Mot-clé mis à jour !')
    } catch (error) {
      console.error('Erreur mise à jour:', error)
      alert('❌ Erreur lors de la mise à jour')
    }
  }

  const toggleActive = async (keywordId: string, currentActive: boolean) => {
    try {
      await lumi.entities.problem_keywords.update(keywordId, {
        active: !currentActive,
        updatedAt: new Date().toISOString()
      })
      await loadKeywords()
    } catch (error) {
      console.error('Erreur toggle:', error)
    }
  }

  const handleDelete = async (keywordId: string) => {
    if (!confirm('Supprimer ce mot-clé ?')) return
    
    try {
      await lumi.entities.problem_keywords.delete(keywordId)
      await loadKeywords()
      alert('✅ Mot-clé supprimé')
    } catch (error) {
      console.error('Erreur suppression:', error)
      alert('❌ Erreur lors de la suppression')
    }
  }

  const startEdit = (keyword: ProblemKeyword) => {
    setEditingId(keyword._id)
    setFormData({
      keyword: keyword.keyword,
      description: keyword.description,
      imageUrl: keyword.imageUrl,
      active: keyword.active
    })
  }

  if (loading) {
    return <div className="text-center py-8">Chargement...</div>
  }

  return (
    <div className={embedded ? '' : 'bg-white rounded-xl shadow-lg p-8 max-w-6xl mx-auto'}>
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">
          Gestion des Mots-clés Problèmes
        </h2>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-lg hover:shadow-lg transition-all"
        >
          <Plus className="w-5 h-5" />
          Ajouter un mot-clé
        </button>
      </div>

      {/* Formulaire d'ajout */}
      {showAddForm && (
        <div className="mb-8 p-6 bg-gray-50 rounded-xl border-2 border-teal-200">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Nouveau mot-clé</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mot-clé *
              </label>
              <input
                type="text"
                value={formData.keyword}
                onChange={(e) => setFormData({ ...formData, keyword: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                placeholder="Ex: Surcharge de travail"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 resize-none"
                rows={3}
                placeholder="Décrivez comment RUGIR peut aider avec ce problème..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Image associée *
              </label>
              {formData.imageUrl ? (
                <div className="space-y-2">
                  <img
                    src={formData.imageUrl}
                    alt="Aperçu"
                    className="w-full h-48 object-cover rounded-lg border-2 border-gray-300"
                  />
                  <button
                    onClick={() => setFormData({ ...formData, imageUrl: '' })}
                    className="text-sm text-red-600 hover:text-red-700"
                  >
                    Supprimer l'image
                  </button>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-lg cursor-pointer hover:from-teal-700 hover:to-cyan-700 transition-all">
                  <Upload className="w-5 h-5" />
                  Uploader une image
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (file) handleFileUpload(file)
                    }}
                    disabled={uploading}
                  />
                </label>
              )}
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                className="w-4 h-4"
              />
              <label className="text-sm font-medium text-gray-700">Actif</label>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleCreate}
                className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Save className="w-4 h-4" />
                Créer
              </button>
              <button
                onClick={() => {
                  setShowAddForm(false)
                  setFormData({ keyword: '', description: '', imageUrl: '', active: true })
                }}
                className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Liste des mots-clés */}
      <div className="space-y-6">
        {keywords.length === 0 ? (
          <p className="text-center text-gray-500 py-8">Aucun mot-clé créé pour le moment</p>
        ) : (
          keywords.map((keyword) => (
            <div key={keyword._id} className="border-2 border-gray-200 rounded-xl p-6 bg-white">
              {editingId === keyword._id ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mot-clé
                    </label>
                    <input
                      type="text"
                      value={formData.keyword}
                      onChange={(e) => setFormData({ ...formData, keyword: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 resize-none"
                      rows={3}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Image
                    </label>
                    <img
                      src={formData.imageUrl}
                      alt={formData.keyword}
                      className="w-full h-48 object-cover rounded-lg border-2 border-gray-300 mb-2"
                    />
                    <label className="inline-flex items-center gap-2 px-4 py-2 bg-teal-100 text-teal-700 rounded-lg cursor-pointer hover:bg-teal-200 transition-all">
                      <Upload className="w-4 h-4" />
                      Changer l'image
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file) handleFileUpload(file, keyword._id)
                        }}
                        disabled={uploading}
                      />
                    </label>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <label className="text-sm font-medium text-gray-700">Actif</label>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleUpdate(keyword._id)}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      <Save className="w-4 h-4" />
                      Enregistrer
                    </button>
                    <button
                      onClick={() => {
                        setEditingId(null)
                        setFormData({ keyword: '', description: '', imageUrl: '', active: true })
                      }}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-6">
                  <img
                    src={keyword.imageUrl}
                    alt={keyword.keyword}
                    className="w-48 h-32 object-cover rounded-lg border-2 border-gray-300"
                  />
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{keyword.keyword}</h3>
                        <p className="text-gray-600">{keyword.description}</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => toggleActive(keyword._id, keyword.active)}
                          className={`p-2 rounded-lg ${
                            keyword.active
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                          }`}
                          title={keyword.active ? 'Désactiver' : 'Activer'}
                        >
                          {keyword.active ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                        </button>
                        <button
                          onClick={() => startEdit(keyword)}
                          className="p-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                          title="Modifier"
                        >
                          <Save className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(keyword._id)}
                          className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                          title="Supprimer"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )
}
